import {
  type SolutionContentSelection,
  isSolutionContentType,
} from './solutionAnalytics';

/**
 * ### SOLUTION_SECTION_SELECTOR
 *
 * Matches every section that reports `view_solution_section`. A section opts
 * in by setting `data-analytics-section` to its `section_id`.
 */
export const SOLUTION_SECTION_SELECTOR = '[data-analytics-section]';

/**
 * ### SOLUTION_CONTENT_SELECTOR
 *
 * Matches every control that may report `select_content` on click. A control
 * opts in with `data-analytics-content-id`, and is only reported once
 * `data-analytics-content-type` and `data-analytics-section-id` are present
 * as well.
 *
 * @see {@link readSolutionContentTarget} Validates the full attribute set
 */
export const SOLUTION_CONTENT_SELECTOR = '[data-analytics-content-id]';

/**
 * ### readSolutionContentTarget
 *
 * Reads the click-tracking attributes of a control into a typed selection.
 * A control missing its id or section, or naming a content type outside
 * {@link SOLUTION_CONTENT_TYPES}, is not reportable and yields `undefined`.
 *
 * @param dataset The control's `dataset`, carrying the `data-analytics-*` attributes
 * @returns The selection to report, or `undefined` when the control is not reportable
 */
export function readSolutionContentTarget(
  dataset: DOMStringMap,
): SolutionContentSelection | undefined {
  const {
    analyticsContentId: contentId,
    analyticsContentType: contentType,
    analyticsSectionId: sectionId,
  } = dataset;

  if (!contentId || !sectionId || !isSolutionContentType(contentType)) {
    return undefined;
  }

  return { contentId, contentType, sectionId };
}
